import {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import xeroConfig, { XERO_CONNECT_PATH } from 'configs/xero.config'

// ---------------------------------------------------------------------------
// Query param keys returned by the auth service on redirect
// ---------------------------------------------------------------------------
export const XERO_SUCCESS_PARAM = 'xeroSuccess'
export const XERO_CUSTOMER_ID_PARAM = 'customerId'
export const XERO_WEBSITE_ID_PARAM = 'websiteId'
export const XERO_ERROR_PARAM = 'xeroError'

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const XeroContext = createContext(null)

export function useXero() {
  const context = useContext(XeroContext)
  if (!context) {
    throw new Error('useXero must be used within a XeroProvider')
  }
  return context
}

// ---------------------------------------------------------------------------
// Parse callback result from current URL search params
// ---------------------------------------------------------------------------
function parseCallbackResult(search) {
  const params = new URLSearchParams(search)
  const xeroSuccess = params.get(XERO_SUCCESS_PARAM)
  const customerId = params.get(XERO_CUSTOMER_ID_PARAM)
  const xeroError = params.get(XERO_ERROR_PARAM)

  if (xeroSuccess === null && !customerId && !xeroError) {
    return null
  }

  return {
    success: xeroSuccess === 'true',
    customerId: customerId || null,
    error: xeroError ? decodeURIComponent(xeroError) : null,
  }
}

// ---------------------------------------------------------------------------
// Build the Connect URL and redirect
// ---------------------------------------------------------------------------
function buildConnectUrl(customerId, websiteId) {
  const base = xeroConfig.authServiceBaseUrl.replace(/\/$/, '')
  const path = XERO_CONNECT_PATH.startsWith('/') ? XERO_CONNECT_PATH : `/${XERO_CONNECT_PATH}`
  const url = new URL(`${base}${path}`)
  url.searchParams.set('customerId', customerId)
  url.searchParams.set('websiteId', websiteId)
  return url.toString()
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function XeroProvider({ children }) {
  const { search, pathname } = useLocation()
  const navigate = useNavigate()

  const callbackResult = useMemo(() => parseCallbackResult(search), [search])

  const connectToXero = useCallback((customerId, options = {}) => {
    if (!customerId || typeof customerId !== 'string') {
      throw new Error('connectToXero requires a non-empty customerId string')
    }

    let websiteId = options.websiteId || options.callbackUrl
    if (!websiteId) {
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const path = options.callbackPath || xeroConfig.defaultCallbackPath
      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      websiteId = `${origin}${normalizedPath}`
    }

    const connectUrl = buildConnectUrl(customerId, websiteId)
    if (typeof window !== 'undefined') {
      window.location.href = connectUrl
    }
  }, [])

  const clearCallbackResult = useCallback(() => {
    if (!callbackResult) return
    const params = new URLSearchParams(search)
    params.delete(XERO_SUCCESS_PARAM)
    params.delete(XERO_CUSTOMER_ID_PARAM)
    params.delete(XERO_WEBSITE_ID_PARAM)
    params.delete(XERO_ERROR_PARAM)
    //const newSearch = params.toString()
    //const newPath = newSearch ? `${pathname}?${newSearch}` : pathname
    //navigate(newPath, { replace: true })
  }, [callbackResult, search, pathname, navigate])

  const value = useMemo(
    () => ({
      connectToXero,
      callbackResult,
      clearCallbackResult,
    }),
    [connectToXero, callbackResult, clearCallbackResult]
  )

  return (
    <XeroContext.Provider value={value}>
      {children}
    </XeroContext.Provider>
  )
}

export default XeroProvider
